/**
 * components/ui/glass.tsx
 * ------------------------------------------------------------------
 * The Al-Nur visual language, reproduced from their actual source
 * (components__MarkEntry.jsx), recoloured to LKC's blue/gold.
 *
 * Al-Nur's "glass" = translucent tinted layers (bg-white/20) +
 * backdrop-blur + faint ring/border at low opacity, over a soft
 * gradient ground; pastel stat chips; rounded-2xl/3xl cards with
 * soft shadows; chunky inputs with a soft focus ring. Full dark
 * mode parity. We keep that recipe exactly; only the brand hues
 * change (their indigo→purple→pink hero becomes LKC blue→gold).
 * ------------------------------------------------------------------
 */
import { useState, useEffect } from 'react';
import type { ReactNode, InputHTMLAttributes } from 'react';
import type { LucideIcon } from 'lucide-react';
import { X } from 'lucide-react';

/* ---- GradientHero ------------------------------------------------
 * Al-Nur: rounded-3xl bg-gradient p-8 text-white shadow-2xl, with
 * two off-edge blurred circles + a faint white overlay, a glass
 * icon tile, and a right-aligned actions slot.
 * LKC palette: deep royal-blue -> lighter blue -> gold sheen.
 * ---------------------------------------------------------------- */
export function GradientHero({
  icon: Icon,
  title,
  subtitle,
  actions,
}: {
  icon: LucideIcon;
  title: string;
  subtitle?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="relative overflow-hidden rounded-3xl bg-[linear-gradient(to_bottom_right,var(--hero-from,#1b3a6b),var(--hero-via,#2d5295),var(--hero-to,#d4a92c))] p-7 sm:p-8 text-white shadow-2xl">
      <div className="absolute inset-0 bg-white/5" />
      <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
      <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/10 rounded-full translate-y-1/2 -translate-x-1/2" />
      <div className="relative flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div className="flex items-center gap-5">
          <div className="w-16 h-16 bg-white/20 backdrop-blur-xl rounded-2xl flex items-center justify-center shadow-lg ring-1 ring-white/30 shrink-0">
            <Icon className="w-8 h-8 text-white" />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">
              {title}
            </h1>
            {subtitle && (
              <p className="text-white/70 mt-1 flex items-center gap-2 text-sm">
                {subtitle}
              </p>
            )}
          </div>
        </div>
        {actions && (
          <div className="relative flex items-center gap-3 flex-wrap">
            {actions}
          </div>
        )}
      </div>
    </div>
  );
}

/* ---- GlassPill ---------------------------------------------------
 * Al-Nur in-hero status pill: bg-{c}-400/20 backdrop-blur-sm
 * border border-{c}-300/30 rounded-xl. Lives ON the hero gradient.
 * ---------------------------------------------------------------- */
const PILL_TONE: Record<string, string> = {
  neutral: 'bg-white/15 border-white/25 text-white',
  amber:
    'bg-amber-400/20 border-amber-300/30 text-amber-100',
  green:
    'bg-green-400/20 border-green-300/30 text-green-100',
  blue: 'bg-blue-400/20 border-blue-300/30 text-blue-100',
  gold: 'bg-[#f0c850]/25 border-[#f0c850]/40 text-[#fff4d6]',
};
export function GlassPill({
  tone = 'neutral',
  icon: Icon,
  children,
}: {
  tone?: keyof typeof PILL_TONE;
  icon?: LucideIcon;
  children: ReactNode;
}) {
  return (
    <div
      className={`flex items-center gap-2 px-4 py-2.5 backdrop-blur-sm border rounded-xl text-sm font-medium ${PILL_TONE[tone]}`}
    >
      {Icon && <Icon size={16} />}
      {children}
    </div>
  );
}

/* ---- SoftCard ----------------------------------------------------
 * Al-Nur: rounded-2xl shadow-sm border border-gray-100 bg-white
 * hover:shadow-md transition-shadow (dark: gray-800/gray-700).
 * ---------------------------------------------------------------- */
export function SoftCard({
  children,
  className = '',
  hover = true,
  pad = 'p-5',
}: {
  children: ReactNode;
  className?: string;
  hover?: boolean;
  pad?: string;
}) {
  return (
    <div
      className={`rounded-2xl border bg-white border-gray-100 shadow-sm dark:bg-gray-800 dark:border-gray-700 ${
        hover ? 'hover:shadow-md transition-shadow' : ''
      } ${pad} ${className}`}
    >
      {children}
    </div>
  );
}

/* ---- StatChip ----------------------------------------------------
 * Al-Nur: rounded-xl p-4 text-center, each a pastel gradient
 * bg-gradient-to-br from-{c}-50 to-{c}-100 with the value in a
 * saturated hue. Dark: bg-{c}-900/40.
 * ---------------------------------------------------------------- */
const CHIP: Record<
  string,
  { bg: string; dark: string; val: string }
> = {
  slate: {
    bg: 'from-slate-50 to-slate-100',
    dark: 'dark:bg-slate-700/50',
    val: 'text-slate-700 dark:text-slate-200',
  },
  blue: {
    bg: 'from-blue-50 to-blue-100',
    dark: 'dark:bg-blue-900/40',
    val: 'text-blue-700 dark:text-blue-300',
  },
  purple: {
    bg: 'from-purple-50 to-purple-100',
    dark: 'dark:bg-purple-900/40',
    val: 'text-purple-700 dark:text-purple-300',
  },
  emerald: {
    bg: 'from-emerald-50 to-emerald-100',
    dark: 'dark:bg-emerald-900/40',
    val: 'text-emerald-700 dark:text-emerald-300',
  },
  green: {
    bg: 'from-green-50 to-green-100',
    dark: 'dark:bg-green-900/40',
    val: 'text-green-700 dark:text-green-300',
  },
  red: {
    bg: 'from-red-50 to-red-100',
    dark: 'dark:bg-red-900/40',
    val: 'text-red-700 dark:text-red-300',
  },
  gold: {
    bg: 'from-amber-50 to-amber-100',
    dark: 'dark:bg-amber-900/40',
    val: 'text-amber-700 dark:text-amber-300',
  },
};
export function StatChip({
  tone = 'slate',
  value,
  label,
  icon: Icon,
}: {
  tone?: keyof typeof CHIP;
  value: ReactNode;
  label: string;
  icon?: LucideIcon;
}) {
  const c = CHIP[tone] ?? CHIP.slate;
  return (
    <div
      className={`rounded-xl p-4 text-center bg-gradient-to-br ${c.bg} ${c.dark}`}
    >
      <div
        className={`text-2xl font-bold flex items-center justify-center gap-1 ${c.val}`}
      >
        {Icon && <Icon size={18} />}
        {value}
      </div>
      <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
        {label}
      </div>
    </div>
  );
}

/* ---- AccentButton ------------------------------------------------
 * Al-Nur "AI Generate": a confident gradient pill. We use the LKC
 * gold→blue accent so it reads as the primary smart action.
 * ---------------------------------------------------------------- */
export function AccentButton({
  icon: Icon,
  children,
  onClick,
  disabled,
  variant = 'accent',
  title,
}: {
  icon?: LucideIcon;
  children: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  variant?: 'accent' | 'soft';
  title?: string;
}) {
  const cls =
    variant === 'accent'
      ? 'bg-[linear-gradient(to_right,var(--accent-from,#2d5295),var(--accent-to,#1b3a6b))] text-white hover:shadow-lg hover:scale-[1.02] shadow-md'
      : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600';
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 ${cls}`}
    >
      {Icon && <Icon size={16} />}
      {children}
    </button>
  );
}

/* ---- GlassInput --------------------------------------------------
 * Al-Nur input: border-2 rounded-xl chunky, soft focus ring
 * (focus:ring-4 focus:ring-{c}-100 focus:border-{c}-400).
 * ---------------------------------------------------------------- */
export function glassFieldClass(extra = '') {
  return `w-full px-4 py-3 border-2 rounded-xl bg-white border-gray-200 text-gray-900 hover:border-gray-300 focus:border-[#2d5295] focus:ring-4 focus:ring-[#1b3a6b]/10 focus:outline-none transition-all dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:hover:border-gray-500 ${extra}`;
}

/* ---- Glass modal ------------------------------------------------
 * Shared frosted-glass modal surface used across all forms. Theme-
 * neutral, so it reads correctly under the academic (navy) and HR
 * (violet) scopes alike. Use <GlassModal> for new forms, or the two
 * class helpers to restyle an existing modal's backdrop/panel.
 * ---------------------------------------------------------------- */
export const glassModalBackdrop =
  'fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm';

export function glassModalSurface(extra = '') {
  return `bg-white/95 dark:bg-gray-900/85 backdrop-blur-xl border border-white/50 dark:border-white/10 ring-1 ring-black/5 rounded-2xl shadow-2xl ${extra}`;
}

export function GlassModal({
  title,
  onClose,
  children,
  footer,
  maxWidth = 'max-w-md',
}: {
  title?: ReactNode;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  maxWidth?: string;
}) {
  return (
    <div className={glassModalBackdrop} onClick={onClose}>
      <div
        className={glassModalSurface(`w-full ${maxWidth} max-h-[90vh] overflow-y-auto`)}
        onClick={(e) => e.stopPropagation()}
      >
        {title !== undefined && (
          <div className="px-5 py-4 border-b border-gray-100/80 dark:border-gray-700/60 flex items-center justify-between">
            <h2 className="font-semibold text-gray-800 dark:text-gray-100">{title}</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-lg leading-none">✕</button>
          </div>
        )}
        <div className="p-5">{children}</div>
        {footer !== undefined && (
          <div className="px-5 py-4 border-t border-gray-100/80 dark:border-gray-700/60 flex items-center justify-end gap-2">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

/* ---- FormModal ---------------------------------------------------
 * The modern entry-form modal: a gradient header (icon + title +
 * optional subtitle + close), a scrollable padded body, and an
 * optional sticky footer for actions. This is the shared shell for
 * all "new / edit X" forms so they share one look.
 * ---------------------------------------------------------------- */
export function FormModal({
  open = true,
  onClose,
  icon: Icon,
  title,
  subtitle,
  children,
  footer,
  maxWidth = 'max-w-2xl',
}: {
  open?: boolean;
  onClose: () => void;
  icon?: LucideIcon;
  title: ReactNode;
  subtitle?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  maxWidth?: string;
}) {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className={`w-full ${maxWidth} bg-white dark:bg-gray-900 rounded-2xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 bg-gradient-to-r from-primary to-primaryDark text-white flex items-center justify-between shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            {Icon && <Icon size={18} className="shrink-0" />}
            <div className="min-w-0">
              <h3 className="font-semibold truncate">{title}</h3>
              {subtitle && <p className="text-xs text-white/80 truncate">{subtitle}</p>}
            </div>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white shrink-0" aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <div className="p-6 overflow-y-auto">{children}</div>

        {footer !== undefined && (
          <div className="px-6 py-4 border-t border-gray-100 dark:border-gray-800 flex items-center justify-end gap-2 shrink-0">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

/* Shared button classes for FormModal footers (and similar). */
export const modalPrimaryBtn =
  'inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-primary hover:bg-primaryDark rounded-xl disabled:opacity-50 transition-colors';
export const modalGhostBtn =
  'px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-colors';


/* ---- NumberField ------------------------------------------------
 * Numeric input that behaves like a normal text box while editing: it
 * can be emptied (no sticky "0"), single digits delete cleanly, and it
 * commits a number only on blur. Emits via onValue (0 when empty).
 * ---------------------------------------------------------------- */
interface NumberFieldProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'type' | 'min' | 'max'> {
  value: number | null | undefined;
  onValue: (n: number) => void;
  integer?: boolean;
  allowNegative?: boolean;
  min?: number;
  max?: number;
}

export function NumberField({
  value, onValue, integer = false, allowNegative = false, min, max, className, onFocus, onBlur, ...rest
}: NumberFieldProps) {
  const fromValue = (v: number | null | undefined) => (v == null || Number.isNaN(v) ? '' : String(v));
  const [text, setText] = useState<string>(fromValue(value));
  const [focused, setFocused] = useState(false);
  useEffect(() => { if (!focused) setText(fromValue(value)); }, [value, focused]);

  const clean = (raw: string) => {
    let v = raw.replace(integer ? /[^0-9-]/g : /[^0-9.-]/g, '');
    if (!integer) { const i = v.indexOf('.'); if (i !== -1) v = v.slice(0, i + 1) + v.slice(i + 1).replace(/\./g, ''); }
    const neg = allowNegative && v.trim().startsWith('-');
    v = (neg ? '-' : '') + v.replace(/-/g, '');
    return v;
  };

  return (
    <input
      {...rest}
      type="text"
      inputMode={integer ? 'numeric' : 'decimal'}
      className={className}
      value={text}
      onFocus={(e) => { setFocused(true); onFocus?.(e); }}
      onChange={(e) => { const v = clean(e.target.value); setText(v); const n = parseFloat(v); onValue(Number.isFinite(n) ? n : 0); }}
      onBlur={(e) => {
        setFocused(false);
        if (text === '' || text === '-' || text === '.') { onValue(0); setText(''); }
        else {
          let n = parseFloat(text);
          if (!Number.isFinite(n)) n = 0;
          if (min != null && n < min) n = min;
          if (max != null && n > max) n = max;
          onValue(n); setText(String(n));
        }
        onBlur?.(e);
      }}
    />
  );
}